/**
 * Prédios da cena, em bloco. São os "alvos" das zonas: o que importa para a
 * decisão é se o prédio está dentro de um anel ou de uma pluma, e que uso
 * ele tem (escola e hospital não se evacuam como uma residência).
 *
 * Referencial da cena: norte = −Z, leste = +X, origem na fonte.
 */

export type UsoPredio = 'residencial' | 'comercial' | 'escola' | 'hospital' | 'industria'

export interface PredioBloco {
  id: string
  nome: string
  /** Centro da base no chão. */
  x: number
  z: number
  larguraM: number
  profundidadeM: number
  alturaM: number
  uso: UsoPredio
}

export const NOMES_USO: Record<UsoPredio, string> = {
  residencial: 'Residencial',
  comercial: 'Comercial',
  escola: 'Escola',
  hospital: 'Hospital',
  industria: 'Indústria',
}

/** Distância horizontal do ponto (x, z) até a face mais próxima do prédio (0 se o ponto está dentro). */
export function distanciaAoPredioM(predio: PredioBloco, x = 0, z = 0): number {
  const dx = Math.max(Math.abs(x - predio.x) - predio.larguraM / 2, 0)
  const dz = Math.max(Math.abs(z - predio.z) - predio.profundidadeM / 2, 0)
  return Math.hypot(dx, dz)
}

/**
 * Cena de beira de rodovia: a pista corre no eixo Z, a fonte fica na origem e
 * há ocupação dos dois lados a distâncias variadas — de 120 m a 1,4 km —
 * para que cada mudança de massa ou de vento mude quem está dentro.
 */
export const PREDIOS_BEIRA_RODOVIA: readonly PredioBloco[] = [
  { id: 'posto', nome: 'Posto de combustível', x: 70, z: 90, larguraM: 30, profundidadeM: 22, alturaM: 6, uso: 'comercial' },
  { id: 'casas-leste', nome: 'Casas (leste)', x: 180, z: -60, larguraM: 60, profundidadeM: 40, alturaM: 6, uso: 'residencial' },
  { id: 'escola', nome: 'Escola municipal', x: -260, z: 140, larguraM: 60, profundidadeM: 35, alturaM: 9, uso: 'escola' },
  { id: 'galpao', nome: 'Galpão industrial', x: -150, z: -320, larguraM: 90, profundidadeM: 50, alturaM: 12, uso: 'industria' },
  { id: 'predio-centro', nome: 'Edifício residencial', x: 420, z: 260, larguraM: 30, profundidadeM: 30, alturaM: 36, uso: 'residencial' },
  { id: 'comercio', nome: 'Centro comercial', x: -520, z: -120, larguraM: 80, profundidadeM: 50, alturaM: 12, uso: 'comercial' },
  { id: 'hospital', nome: 'Hospital regional', x: 680, z: -420, larguraM: 70, profundidadeM: 50, alturaM: 24, uso: 'hospital' },
  { id: 'bairro-oeste', nome: 'Bairro (oeste)', x: -900, z: 300, larguraM: 120, profundidadeM: 90, alturaM: 6, uso: 'residencial' },
  { id: 'bairro-norte', nome: 'Bairro (norte)', x: 150, z: -1150, larguraM: 140, profundidadeM: 80, alturaM: 6, uso: 'residencial' },
  { id: 'escola-sul', nome: 'Escola estadual', x: -60, z: 1350, larguraM: 60, profundidadeM: 40, alturaM: 9, uso: 'escola' },
]

/**
 * Pontos baixos da cena — bueiros da drenagem da rodovia e o córrego. Gás
 * denso escoa e acumula ali, longe de onde a pluma gaussiana diz que ele está.
 */
export interface PontoBaixo {
  id: string
  nome: string
  tipo: 'bueiro' | 'corrego'
  /** Pontos (x, z) — um só para bueiro, a linha do leito para córrego. */
  pontos: ReadonlyArray<readonly [number, number]>
}

export const PONTOS_BAIXOS: readonly PontoBaixo[] = [
  { id: 'bueiro-1', nome: 'Bueiro (acostamento)', tipo: 'bueiro', pontos: [[7, 35]] },
  { id: 'bueiro-2', nome: 'Bueiro (acostamento)', tipo: 'bueiro', pontos: [[-7, -60]] },
  { id: 'bueiro-3', nome: 'Bueiro (acostamento)', tipo: 'bueiro', pontos: [[7, -140]] },
  {
    id: 'corrego',
    nome: 'Córrego (baixada)',
    tipo: 'corrego',
    pontos: [[-600, -260], [-380, -210], [-200, -170], [-40, -150], [120, -175], [300, -230], [520, -260], [800, -240]],
  },
]
